import { useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import NoData from "components/NoData/NoData.view";
import { useStyles } from "./DowntimeSummaryByProductionLine.styles";
import { GroupDataByProductionLine } from "./DowntimeSummaryByProductionLineInterfaces";
import { UptimeDowntimeBar } from "./UptimeDowntimeBar/UptimeDowntimeBar.view";
import { truncateLongText } from "utilities/utilities";
import { TrendReportContext } from "contexts/TrendReportContext/TrendReportContext";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";

const DowntimeSummaryByProductionLine = () => {
    const classes = useStyles();
    const {
        showAllProductionLines,
        handleSelectedProductionLineChange,
        handleSelectedPreviousProductionLineChange,
        selectedProductionLine,
    } = useContext(TrendReportContext);
    const { millId } = useParams();

    const { reportRangeStartDate: startDate, reportRangeEndDate: endDate } = useContext(MillDashboardContext);

    const queryFilters = useMemo(
        () => ({
            startDate,
            endDate,
        }),
        [endDate, startDate],
    );

    const { downtimeSummaryPerMachineCenter, uptimeSummaryPerMachineCenter } =
        MillsEntity.GetUptimesVsDowntimesTotalsPerMachineCenterMillDashboard(queryFilters, Number(millId));

    const groupedDataByProductionLine: GroupDataByProductionLine = useMemo(
        () =>
            downtimeSummaryPerMachineCenter.reduce((groups: GroupDataByProductionLine, obj) => {
                const productionLineId = obj.production_line.production_line_id;
                const productionLineName = obj.production_line.code || obj.production_line.name;
                const uptimeSummary = uptimeSummaryPerMachineCenter.find(
                    (machineCenter) => machineCenter.machine_center_id === obj.machine_center_id,
                );

                if (!groups[productionLineId]) {
                    groups[productionLineId] = {
                        productionLineId: productionLineId,
                        productionLineName: productionLineName,
                        downtime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                        uptime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                    };
                }

                groups[productionLineId].downtime.seconds += obj.summary.aggregate.sum.seconds;
                groups[productionLineId].downtime.occurrences += obj.summary.aggregate.sum.occurrences;
                if (uptimeSummary) {
                    groups[productionLineId].uptime.seconds += uptimeSummary.summary.aggregate.sum.seconds;
                    groups[productionLineId].uptime.occurrences += uptimeSummary.summary.aggregate.sum.occurrences;
                }

                return groups;
            }, {}),
        [downtimeSummaryPerMachineCenter, uptimeSummaryPerMachineCenter],
    );

    return Object.values(groupedDataByProductionLine).length > 0 ? (
        <Box className={classes.mainWrapper}>
            <section className={classes.leftSection}>
                {Object.values(groupedDataByProductionLine).length >= 1 && (
                    <div>
                        {Object.values(groupedDataByProductionLine).map((machineCenter, index) => {
                            return (
                                <UptimeDowntimeBar
                                    key={`${index + Math.random()}`}
                                    onClick={() => {
                                        handleSelectedProductionLineChange({
                                            id: machineCenter.productionLineId,
                                            name: machineCenter.productionLineName,
                                        });
                                        handleSelectedPreviousProductionLineChange({
                                            id: machineCenter.productionLineId,
                                            name: machineCenter.productionLineName,
                                        });
                                    }}
                                    selected={selectedProductionLine?.id === machineCenter.productionLineId}
                                    allProductionLinesSelected={showAllProductionLines}
                                    productionLineName={truncateLongText(machineCenter.productionLineName, 12)}
                                    uptimeValue={machineCenter.uptime.seconds / 60}
                                    downtimeValue={machineCenter.downtime.seconds / 60}
                                />
                            );
                        })}
                    </div>
                )}
            </section>
        </Box>
    ) : (
        <NoData className={classes.noData} />
    );
};

export default DowntimeSummaryByProductionLine;
